import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { useSessao } from "@/sessao/useSessao";
import { useAmostra, useFilas, useReprocessarFilaMorta } from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import type { AmostraDaFilaMorta } from "@/api/contrato";
import { Botao } from "@/componentes/Botao";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { useAvisos } from "@/componentes/Avisos";
import { Dica } from "@/componentes/Dica";
import { Confirmacao, type PedidoDeConfirmacao } from "@/componentes/Confirmacao";
import { contagem, dataHora } from "@/formato";

/*
 * A fila morta — doc 06 §4.3.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ AGRUPADA POR CAUSA RAIZ, E NÃO ITEM A ITEM.                              │
 * │                                                                          │
 * │ "O fluxo real é: um convênio quebrou às 3h, 400 boletos caíram, conserta │
 * │ e reprocessa os 400." Uma lista de quatrocentas linhas idênticas não     │
 * │ ajuda ninguém a ver que são quatrocentas do mesmo problema.              │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Espiar e reprocessar têm garantias OPOSTAS, de propósito: a amostra devolve
 * tudo o que lê, esta ação consome. Uma diagnostica, a outra conserta.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A REQUISIÇÃO LEVA O GRUPO QUE O OPERADOR CONFIRMOU.                      │
 * │                                                                          │
 * │ O grupo e o total são gravados no clique, e não relidos na confirmação:  │
 * │ a amostra é uma foto, e o que vale é o número que ele digitou. Sem causa │
 * │ no corpo a API move tudo o que ler, e por isso o grupo sem causa vai com │
 * │ `sem_causa`, e nunca com causa vazia.                                    │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Quem decide se o fluxo aceita é o servidor: a tela compara `reprocessavel` com
 * `true`, e assim painel novo sobre API velha não oferece o botão.
 */
type GrupoConfirmado = Pick<
  NonNullable<AmostraDaFilaMorta["grupos"]>[number],
  "causa" | "causa_presente" | "total"
>;

export function FilaMorta() {
  const { cliente, estado, pode } = useSessao();
  const avisos = useAvisos();

  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [pedido, setPedido] = useState<PedidoDeConfirmacao | null>(null);
  const [grupoEmFoco, setGrupoEmFoco] = useState<GrupoConfirmado | null>(null);

  const { data: filas, error, isPending } = useFilas(cliente, estado.fase === "dentro");
  const { data: amostra, error: erroDaAmostra } = useAmostra(cliente, escolhida);
  const reprocessar = useReprocessarFilaMorta(cliente);
  const podeOperar = pode("operador");

  if (isPending) {
    return <div aria-hidden="true" className="h-40 rounded-cartao bg-superficie" />;
  }

  if (error) {
    return (
      <p role="alert" className="rounded-cartao border border-falha/40 bg-falha/10 px-4 py-3 text-falha">
        {textoDoErro(error)}
      </p>
    );
  }

  // "Não configurado" e "indisponível" levam a ações OPOSTAS — um se resolve
  // editando um Secret, o outro chamando quem cuida do broker.
  if (filas?.estado !== "ok") {
    return (
      <p className="rounded-cartao border border-fio bg-superficie px-4 py-3 text-texto-2">
        {filas?.estado === "nao_configurado"
          ? "Broker não configurado neste ambiente."
          : "Não foi possível ler o broker."}
      </p>
    );
  }

  const fluxos = filas.fluxos ?? [];
  const reprocessavel =
    fluxos.find((fluxo) => fluxo.fila_morta === escolhida)?.reprocessavel === true;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
        {fluxos.map((fluxo) => {
          const morta = fluxo.morta ?? 0;
          const temAusente = (fluxo.ausentes?.length ?? 0) > 0;

          return (
            <button
              key={fluxo.fluxo}
              type="button"
              onClick={() => {
                setEscolhida(fluxo.fila_morta ?? null);
                setGrupoEmFoco(null);
              }}
              className={[
                "rounded-cartao border bg-superficie p-3 text-left",
                escolhida === fluxo.fila_morta
                  ? "border-esferografica"
                  : "border-fio hover:border-fio-forte",
              ].join(" ")}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-corpo text-texto">{fluxo.fluxo}</span>
                <span
                  className={`dados text-titulo font-medium ${morta > 0 ? "text-falha" : "text-texto-3"}`}
                >
                  {morta}
                </span>
              </div>

              <div className="mt-1 flex gap-3 text-[12px] text-texto-2">
                <span>
                  trabalho <span className="dados">{fluxo.trabalho ?? 0}</span>
                </span>
                <span>
                  retry <span className="dados">{fluxo.retry ?? 0}</span>
                </span>
              </div>

              {temAusente && (
                /*
                  Fila ausente é diferente de fila vazia: vazia é o estado
                  saudável, ausente significa broker recriado sem topologia — e
                  mensagem publicada numa fila que não existe não espera em
                  lugar nenhum.
                */
                <p className="mt-2 text-[12px] text-transito">
                  {contagem(fluxo.ausentes?.length ?? 0, "fila declarada", "filas declaradas")} que o broker não tem.
                </p>
              )}
            </button>
          );
        })}
      </div>

      {escolhida === null ? (
        <p className="flex items-center gap-1 text-texto-2">
          Escolha um fluxo.
          <Dica texto="Espiar não consome mensagem: tudo o que for lido volta para a fila." />
        </p>
      ) : erroDaAmostra ? (
        <p role="alert" className="text-falha">
          {textoDoErro(erroDaAmostra)}
        </p>
      ) : amostra?.estado !== "ok" ? (
        <p className="text-texto-2">Não foi possível espiar esta fila agora.</p>
      ) : (amostra.grupos?.length ?? 0) === 0 ? (
        <p className="text-texto-2">
          Nenhuma mensagem em <span className="dados">{escolhida}</span>.
        </p>
      ) : (
        <section className="flex flex-col gap-2">
          <header className="flex items-baseline gap-2">
            <h2 className="text-titulo font-semibold">Agrupada por causa</h2>
            <span className="rotulo">
              {contagem(amostra.lidas ?? 0, "lida", "lidas")} de <span className="dados">{amostra.fila}</span>
            </span>
            {podeOperar && !reprocessavel && (
              <Dica texto="Esta fila não aceita reprocessamento: reenviar as mensagens pode duplicar o efeito." />
            )}
          </header>

          {amostra.grupos?.map((grupo) => (
            <article
              key={grupo.causa}
              className="rounded-cartao border border-fio bg-superficie p-3"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-corpo text-texto">
                    {grupo.causa_presente ? (
                      grupo.causa
                    ) : (
                      // Chegou sem causa: diferente de causa vazia, e o
                      // diagnóstico é outro — a mensagem não passou pelo
                      // caminho que carimba o motivo.
                      <span className="text-texto-2">sem causa registrada</span>
                    )}
                  </p>
                  <p className="rotulo mt-1">{contagem(grupo.total, "mensagem", "mensagens")}</p>
                </div>

                {podeOperar && reprocessavel && (
                  <Botao
                    tom="secundario"
                    onClick={() => {
                      setGrupoEmFoco({
                        causa: grupo.causa,
                        causa_presente: grupo.causa_presente,
                        total: grupo.total,
                      });
                      setPedido({
                        severidade: "critica",
                        titulo: `Reprocessar ${contagem(grupo.total, "mensagem", "mensagens")}?`,
                        corpo: (
                          <>
                            Voltam à fila de trabalho com tentativas zeradas; sem a causa
                            corrigida, falham de novo e gastam orçamento do provider.
                          </>
                        ),
                        verbo: "Reprocessar grupo",
                        digitarParaLiberar: String(grupo.total),
                      });
                    }}
                  >
                    <RefreshCw aria-hidden="true" className="size-4" />
                    Reprocessar grupo
                  </Botao>
                )}
              </div>

              {(grupo.exemplos?.length ?? 0) > 0 && (
                <div className="mt-3 grid gap-2 md:grid-cols-2">
                  {grupo.exemplos?.map((exemplo) => (
                    <CaixaDeCampo
                      key={exemplo.message_id ?? Math.random()}
                      rotulo={`${exemplo.tipo ?? "mensagem"} · ${contagem(exemplo.tentativas ?? 0, "tentativa", "tentativas")}`}
                      dados
                    >
                      <span className="block truncate" title={exemplo.message_id}>
                        {exemplo.message_id ?? "—"}
                      </span>
                      <span className="block text-[11px] text-texto-3">
                        {dataHora(exemplo.publicada_em)}
                      </span>
                    </CaixaDeCampo>
                  ))}
                </div>
              )}
            </article>
          ))}
        </section>
      )}

      <Confirmacao
        pedido={pedido}
        aoCancelar={() => {
          setPedido(null);
          setGrupoEmFoco(null);
        }}
        aoConfirmar={async () => {
          if (escolhida === null || grupoEmFoco === null) return;

          const relatorio = await reprocessar.mutateAsync({
            fila: escolhida,
            ...(grupoEmFoco.causa_presente ? { causa: grupoEmFoco.causa } : { semCausa: true }),
            limite: grupoEmFoco.total,
          });
          setPedido(null);
          setGrupoEmFoco(null);

          /*
           * O relatório pode ser PARCIAL, e a tela diz isso. "Movidas 120, pode
           * haver mais" é uma informação diferente de "movidas 120" — a segunda
           * faria o operador achar que terminou. "Pode haver", e não "restam":
           * o grupo pode ter acabado exatamente no limite.
           */
          const partes = [contagem(relatorio?.movidas ?? 0, "movida", "movidas")];
          if ((relatorio?.ignoradas ?? 0) > 0) {
            partes.push(
              `${contagem(relatorio?.ignoradas ?? 0, "devolvida", "devolvidas")} por não casar com a causa`,
            );
          }
          if (relatorio?.restam === true) partes.push("pode haver mais mensagens");
          if (relatorio?.falha) partes.push(`interrompido: ${relatorio.falha}`);

          avisos.feito("Fila morta reprocessada", partes.join(" · "));
        }}
      />
    </div>
  );
}
